import type { GetToken } from "./canvasService";
import { apiUrl } from "@/config/api";
import { tracedFetch } from "@/utils/tracedFetch";

export interface ApiForge {
  id: string;
  title: string;
  researchQuestion: string;
}

export interface ApiSubject {
  id: string;
  title: string;
  forges: ApiForge[];
}

export interface ApiProject {
  id: string;
  name: string;
  icon?: string;
  subjects: ApiSubject[];
}

// Wire types (backend snake_case JSON)
interface WireForge {
  id: string;
  title: string;
  research_question: string;
}

interface WireSubject {
  id: string;
  title: string;
  forges: WireForge[];
}

interface WireProject {
  id: string;
  name: string;
  icon?: string;
  subjects: WireSubject[];
}

function mapForge(w: WireForge): ApiForge {
  return { id: w.id, title: w.title, researchQuestion: w.research_question };
}

async function authedFetch(url: string, getToken: GetToken, init?: RequestInit): Promise<Response> {
  const token = await getToken();
  const res = await tracedFetch(apiUrl(url), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${body}`);
  }
  return res;
}

export async function listProjects(getToken: GetToken): Promise<ApiProject[]> {
  const res = await authedFetch("/api/projects", getToken);
  const wire = (await res.json()) as WireProject[];
  return wire.map((p) => ({
    id: p.id,
    name: p.name,
    icon: p.icon,
    subjects: (p.subjects ?? []).map((s) => ({
      id: s.id,
      title: s.title,
      forges: (s.forges ?? []).map(mapForge),
    })),
  }));
}

export async function createProject(getToken: GetToken, name: string): Promise<ApiProject> {
  const res = await authedFetch("/api/projects", getToken, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  const wire = (await res.json()) as WireProject;
  return { id: wire.id, name: wire.name, subjects: [] };
}

export async function renameProject(getToken: GetToken, projectId: string, name: string): Promise<void> {
  await authedFetch(`/api/projects/${projectId}`, getToken, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export async function setProjectIcon(getToken: GetToken, projectId: string, icon: string): Promise<void> {
  await authedFetch(`/api/projects/${projectId}/icon`, getToken, {
    method: "PATCH",
    body: JSON.stringify({ icon }),
  });
}

export async function createSubject(getToken: GetToken, projectId: string, title: string): Promise<ApiSubject> {
  const res = await authedFetch(`/api/projects/${projectId}/subjects`, getToken, {
    method: "POST",
    body: JSON.stringify({ title }),
  });
  const wire = (await res.json()) as WireSubject;
  return { id: wire.id, title: wire.title, forges: [] };
}

export async function renameSubject(getToken: GetToken, subjectId: string, title: string): Promise<void> {
  await authedFetch(`/api/subjects/${subjectId}`, getToken, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
}

export async function deleteProject(getToken: GetToken, projectId: string): Promise<void> {
  await authedFetch(`/api/projects/${projectId}`, getToken, { method: "DELETE" });
}

export async function deleteSubject(getToken: GetToken, subjectId: string): Promise<void> {
  await authedFetch(`/api/subjects/${subjectId}`, getToken, { method: "DELETE" });
}

export async function createForge(getToken: GetToken, subjectId: string, title: string): Promise<ApiForge> {
  const res = await authedFetch(`/api/subjects/${subjectId}/forges`, getToken, {
    method: "POST",
    body: JSON.stringify({ title }),
  });
  const wire = (await res.json()) as WireForge & { subject_id?: string };
  return mapForge(wire);
}

export async function renameForge(getToken: GetToken, forgeId: string, title: string): Promise<void> {
  await authedFetch(`/api/forges/${forgeId}/title`, getToken, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
}

export async function deleteForge(getToken: GetToken, forgeId: string): Promise<void> {
  await authedFetch(`/api/forges/${forgeId}`, getToken, { method: "DELETE" });
}
